import React, { useEffect, useState } from 'react';
import { Mail, Phone, MessageSquare, CheckCircle, XCircle, Clock, Trash2, Eye, Filter, Reply } from 'lucide-react';
import config from '../utils/config';

export default function JoinUsAdmin() {
  const [joinus, setJoinus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => { fetchJoinUs(); }, []);

  const fetchJoinUs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${config.url}/joinus`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setJoinus(data);
    } catch (error) {
      setError('Failed to fetch join us data');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${config.url}/joinus/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        setSuccess('Status updated successfully!');
        fetchJoinUs();
      } else {
        setError('Failed to update status');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${config.url}/joinus/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSuccess('Entry deleted successfully!');
        fetchJoinUs();
      } else {
        setError('Failed to delete entry');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'unread': return 'bg-red-100 text-red-800';
      case 'read': return 'bg-yellow-100 text-yellow-800';
      case 'replied': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  const getStatusIcon = (status) => {
    switch (status) {
      case 'unread': return <Clock className="w-4 h-4" />;
      case 'read': return <CheckCircle className="w-4 h-4" />;
      case 'replied': return <Reply className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };
  const filteredJoinus = selectedStatus === 'all' ? joinus : joinus.filter(entry => entry.status === selectedStatus);
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-IN');
  const formatTime = (dateString) => new Date(dateString).toLocaleTimeString('en-IN');

  return (
    <div className="space-y-6 hindi-text">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Join Us Management</h1>
          <p className="text-gray-600">Manage join us form submissions</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">All Status</option>
              <option value="unread">Unread</option>
              <option value="read">Read</option>
              <option value="replied">Replied</option>
            </select>
          </div>
        </div>
      </div>
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700">{success}</p>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredJoinus.map(entry => (
          <div key={entry.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg text-gray-900">{entry.name}</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getStatusColor(entry.status)}`}>
                  {getStatusIcon(entry.status)}
                  <span className="capitalize">{entry.status}</span>
                </span>
              </div>
              <div className="space-y-3 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <Mail className="w-4 h-4 mr-2" />
                  {entry.email}
                </div>
                {entry.phone && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Phone className="w-4 h-4 mr-2" />
                    {entry.phone}
                  </div>
                )}
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Date:</span> {formatDate(entry.created_at)}
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => { setSelectedEntry(entry); setShowDetails(true); }}
                  className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-1"
                >
                  <Eye className="w-4 h-4" />
                  <span>View</span>
                </button>
                <button
                  onClick={() => handleDelete(entry.id)}
                  className="flex-1 bg-red-50 hover:bg-red-100 text-red-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-1"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete</span>
                </button>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                {entry.status === 'unread' && (
                  <button
                    onClick={() => handleStatusUpdate(entry.id, 'read')}
                    className="bg-yellow-50 hover:bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs font-medium transition-colors"
                  >
                    Mark Read
                  </button>
                )}
                {entry.status === 'read' && (
                  <button
                    onClick={() => handleStatusUpdate(entry.id, 'replied')}
                    className="bg-green-50 hover:bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium transition-colors"
                  >
                    Mark Replied
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredJoinus.length === 0 && !loading && (
        <div className="text-center py-12">
          <MessageSquare className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No join us entries found</h3>
          <p className="text-gray-600">No join us entries match the selected criteria</p>
        </div>
      )}
      {showDetails && selectedEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Join Us Details</h2>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <p className="text-gray-900">{selectedEntry.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <p className="text-gray-900">{selectedEntry.email}</p>
                  </div>
                  {selectedEntry.phone && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <p className="text-gray-900">{selectedEntry.phone}</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedEntry.status)}`}>
                      {selectedEntry.status}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-900 whitespace-pre-wrap">{selectedEntry.message}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Received On</label>
                    <p className="text-gray-900">{formatDate(selectedEntry.created_at)} at {formatTime(selectedEntry.created_at)}</p>
                  </div>
                  {selectedEntry.updated_at !== selectedEntry.created_at && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                      <p className="text-gray-900">{formatDate(selectedEntry.updated_at)} at {formatTime(selectedEntry.updated_at)}</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowDetails(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}